import {CITY_CAPTURE_ANIMATION, getCityCaptureAnimationDurationMs} from '../configs/cityCaptureAnimation';
import {finishCityCaptureAnimation, startCityCaptureAnimation} from '../redux/actions';
import {selectGameType, selectUnits} from '../redux/reducers/sharedStateReducer/selectors';
import {GAME_TYPES} from '../constants/game';
import {FIELD_ANIMATED_UP_TO_SPEED_MULTIPLIER} from '../configs/playbackSpeed';
import {checkIfReplayRewinding, getReplayPlaybackSpeedMultiplier} from '../utils/replay/replayPlaybackSpeed';
import {markAwaitedAnimationFinished, markAwaitedAnimationPlaying} from '../utils/replay/replayAwaitedAnimations';
import {BOT_ACTION_INTERVAL_MS} from '../utils/bots/botConfig';
import {getBotActionIntervalMs} from '../utils/bots/botActionPace';
import {getPlayer0xColor} from '../utils/players/getPlayer0xColor';
import {checkIfPageHidden} from '../utils/sound/checkIfSoundOutputAsleep';
import {CITY_CAPTURE_LAYER_DEPTHS} from '../utils/HexGridRenderer';
import {
  buildCityCaptureLayerOwners,
  buildCityCaptureOverrides,
  buildCityCaptureSteps,
  calcCityCaptureBurnRadii,
  calcCityCaptureCameraFrame,
  calcCityCaptureIconSwapShare,
  buildRaggedRingPoints,
  calcHexListBounds,
  clampCameraFrameToBounds,
  collectCityCaptureAffectedHexKeys,
  easeInOutSine,
  getCityCaptureBurnSoundId,
  interpolateCameraFrame,
  orderCityCaptureSteps,
} from './cityCaptureAnimationPlan';

// Облет оборван (сторож, уничтожение сцены): ожидающие шаги сценария
// отклоняются этим значением, и сценарий молча выходит
const CANCELLED = Symbol('city-capture-animation-cancelled');

const SPARK_TEXTURE_KEY = 'city_capture_spark';
const ICON_TEXTURE_PREFIX = 'city_capture_icon:';
const NEW_OWNER_COLOR = 'newOwner';

// Ключ облета среди анимаций, которых ждет проигрыватель записи матча
// (utils/replay/replayAwaitedAnimations)
const AWAITED_ANIMATION_KEY = 'city-capture';

const getIconTextureKey = (iconPath) => `${ICON_TEXTURE_PREFIX}${iconPath}`;

/**
 * Облет захваченных городов в конце раунда. Камера по очереди подъезжает к
 * каждому захваченному городу, и на нем огненный фронт «прожигает» город из
 * цвета прежнего владельца в цвет нового: граница, заливка ячеек и иконка
 * владельца над центральным кварталом. После последнего города показывается
 * модалка захвата, а камера под ней возвращается к кадру игрока. Пока идет
 * облет, поле не двигается, не зумится и не кликается (см.
 * isFieldInputLocked в GameScene).
 *
 * Запускает облет middleware (cityCaptureAnimationMiddleware) сразу после
 * редьюсера захвата: флаг START_CITY_CAPTURE_ANIMATION держит модалку захвата
 * и следующие за ней в очереди. Гасит флаг только этот класс — в конце, при
 * обрыве и по сторожевому таймеру, — чтобы модалка не застряла.
 *
 * Состояние на поле уже «после захвата», поэтому до своей очереди город
 * показан прежним владельцем подменой при отрисовке
 * (HexGridRenderer.setCityCaptureOverrides). Текущий город рисуют слои
 * анимации: граница «до» и «после» тем же кодом, что обычная граница, под
 * масками круга прожигания.
 */
export class CityCaptureAnimation {
  constructor(scene, config = CITY_CAPTURE_ANIMATION) {
    this.scene = scene;
    this.config = config;
    this.run = null;
  }

  /** Облет идет — в том числе возвращает камеру после последнего города */
  get isActive() {
    return Boolean(this.run);
  }

  /** Камера и клики по полю принадлежат облету */
  get isInputLocked() {
    return Boolean(this.run?.isInputLocked);
  }

  /**
   * Можно ли показывать облет. Зритель и игрок, скрывший всплывающие окна,
   * облет видят: это не всплывающее окно, а само поле, — скрыта у них только
   * модалка захвата после него.
   *
   * Запись матча (страница записи и витрина главного меню) — по правилу
   * остальных анимаций поля (FIELD_ANIMATED_UP_TO_SPEED_MULTIPLIER): до x5
   * облет идет целиком, и проигрыватель ждет его конца (см. start), а быстрее
   * — уже перемотка, облета нет; во время самой перемотки — тоже. Партия
   * ботов на витрине меню — по тому же порогу скорости ее ползунка.
   *
   * Сетевая партия: облет укладывается в окно во времени хода
   * (cityCaptureTimeWindow), и боты ждут только это окно, а не облет у
   * каждого клиента — иначе одна скрытая вкладка задерживала бы матч всем.
   * В скрытой вкладке кадров нет: облет встал бы и проигрался после
   * возвращения, когда окно давно кончилось и боты уже ходят у него на фоне.
   * Поэтому в скрытой вкладке облета нет — сразу модалка захвата (см. и
   * watchNetworkWindow)
   */
  canPlay(state) {
    const {scene} = this;
    if (!scene.isSceneActive || !scene.cameras?.main || !scene.hexGridRenderer) {
      return false;
    }
    if (selectGameType(state) === GAME_TYPES.ONLINE && checkIfPageHidden()) {
      return false;
    }
    if (selectGameType(state) === GAME_TYPES.REPLAY) {
      return !checkIfReplayRewinding()
        && getReplayPlaybackSpeedMultiplier() <= FIELD_ANIMATED_UP_TO_SPEED_MULTIPLIER;
    }
    if (scene.isDemoMode) {
      return BOT_ACTION_INTERVAL_MS / getBotActionIntervalMs() <= FIELD_ANIMATED_UP_TO_SPEED_MULTIPLIER;
    }
    return true;
  }

  /**
   * Запустить облет по захватам `capturedCities` (gameState.lastCapturedCities)
   * @returns {boolean} запущен ли облет; нет — модалка захвата покажется сразу
   */
  start(capturedCities) {
    if (this.run) {
      return false;
    }
    const {scene} = this;
    const state = scene.store.getState();
    if (!this.canPlay(state)) {
      return false;
    }
    const renderer = scene.hexGridRenderer;
    const steps = buildCityCaptureSteps(capturedCities, selectUnits(state))
      .filter((step) => step.cityHexKeys.some((hexKey) => renderer.hexes[hexKey]));
    if (steps.length === 0) {
      return false;
    }

    const run = {
      steps,
      pending: new Set(),
      objects: new Set(),
      masks: new Set(),
      isInputLocked: false,
      isCancelled: false,
      isQueueFinished: false,
      watchdog: null,
      savedCamera: null,
      onVisibilityChange: null,
    };
    this.run = run;
    this.watchNetworkWindow(run, state, capturedCities);
    // Пока облет не дошел до города, город показан прежним владельцем.
    // Подмена ставится ДО флага: флаг перерисует поле уже с ней
    this.applyOverrides(run, {pendingSteps: steps}, {redraw: false});
    scene.store.dispatch(startCityCaptureAnimation());
    // Проигрыватель записи матча не идет дальше, пока облет на поле, — но не
    // дольше его срока: в скрытой вкладке кадры не идут, и облет может встать
    markAwaitedAnimationPlaying(AWAITED_ANIMATION_KEY, Date.now() + this.getMaxDurationMs(run));

    this.play(run).catch((error) => {
      if (error === CANCELLED) {
        return;
      }
      console.warn('[CityCapture] облет захваченных городов оборвался', error);
      this.abort({restoreCamera: true});
    });
    return true;
  }

  async play(run) {
    const {scene, config} = this;
    // Облет идет сразу, ничего не дожидаясь: итоги боя, пришедшие вместе с
    // захватом, уступают ему и модалке захвата (см.
    // selectIsCombatResultsModalVisible) — иначе затянутая модалка боя
    // отодвинула бы облет на ход нового раунда, когда идет таймер игрока
    run.isInputLocked = true;
    scene.stopCameraPan?.();
    run.savedCamera = scene.getCameraFrame();
    this.armWatchdog(run);
    // Иконки владельцев загружаются и декодируются до первого переезда, а не
    // во время него: на телефоне это тоже рывки камеры
    await this.guard(run, this.loadIconTextures(run));

    const steps = orderCityCaptureSteps(run.steps, run.savedCamera, (step) => this.getStepCenter(step));
    for (let index = 0; index < steps.length; index += 1) {
      const step = steps[index];
      await this.settle(run);
      await this.moveCamera(run, this.getStepCameraFrame(step) || scene.getCameraFrame(), config.cameraMoveMs);
      await this.playStep(run, step, steps.slice(index + 1));
    }

    this.finishQueue(run);
    await this.settle(run);
    await this.moveCamera(run, run.savedCamera, config.restoreCameraMs);
    this.cleanup(run);
  }

  /**
   * Один город: проявление в цвете прежнего владельца, пауза, прожигание,
   * пауза в цвете нового и угасание заливки с иконкой
   * @param {Object} run
   * @param {Object} step
   * @param {Object[]} pendingSteps - города после этого
   */
  async playStep(run, step, pendingSteps) {
    const {scene, config} = this;
    const renderer = scene.hexGridRenderer;
    const {before, after} = buildCityCaptureLayerOwners(renderer.getCityBorderBaseOwners(), step, pendingSteps);
    const affectedHexKeys = collectCityCaptureAffectedHexKeys(step, before, after)
      .filter((hexKey) => renderer.hexes[hexKey]);
    const layers = this.createStepLayers(run, step, {before, after, affectedHexKeys});

    // Обычная граница уступает ячейки города и их соседей слоям анимации —
    // слой «до» нарисован так же, как она, и подмена проходит без скачка.
    // Поле не пересобирается: граница прячется на месте
    const overrides = {pendingSteps, currentStep: step, hiddenBorderHexKeys: affectedHexKeys};
    this.applyOverrides(run, overrides, {
      redrawInPlace: () => {
        renderer.setCityBorderCellsVisible(affectedHexKeys, false);
        return true;
      },
    });

    await this.runTween(run, config.introMs, (t) => {
      layers.beforeFill.setAlpha(t);
      layers.fromIcon?.setAlpha(t);
    });
    await this.runTween(run, config.beforeBurnPauseMs);

    let isTokenSwapped = false;
    this.playBurnSound(step);
    await this.runTween(run, config.burnMs, (t) => {
      const radius = layers.radii.start + (layers.radii.end - layers.radii.start) * t;
      this.drawBurnFront(layers, radius, (t * config.burnMs * config.fire.wobblePerSecond) / 1000);
      this.emitSparks(layers, radius);
      const swapShare = calcCityCaptureIconSwapShare(t, config);
      layers.fromIcon?.setAlpha(1 - swapShare);
      layers.toIcon?.setAlpha(swapShare);
      // Флаг центрального квартала меняется под иконкой, пока она меняется, —
      // перерисовкой одного жетона: полная пересборка поля посреди огня
      // давала рывок
      if (!isTokenSwapped && swapShare > 0) {
        isTokenSwapped = true;
        this.applyOverrides(run, {...overrides, isCurrentTokenSwapped: true}, {
          redrawInPlace: () => renderer.rerenderHexToken(step.centralQuarterHexKey, scene.store.getState()),
        });
      }
    });
    this.completeBurn(run, layers);

    await this.runTween(run, config.afterBurnHoldMs);
    await this.runTween(run, config.outroMs, (t) => {
      layers.afterFill.setAlpha(1 - t);
      layers.toIcon?.setAlpha(1 - t);
    });

    // Город показан: его ячейки снова рисует обычная граница — уже за нового
    // владельца, ровно как слой «после». Перерисовываются только они
    this.applyOverrides(run, {pendingSteps}, {
      redrawInPlace: () => {
        renderer.redrawCityBorderCells(affectedHexKeys);
        return true;
      },
    });
    this.destroyObjects(run, layers.objects);
  }

  /**
   * Слои города: граница и заливка «до» (снаружи круга прожигания) и «после»
   * (внутри него), огонь фронта, искры и иконки владельцев
   */
  createStepLayers(run, step, {before, after, affectedHexKeys}) {
    const {scene, config} = this;
    const renderer = scene.hexGridRenderer;
    const objects = [];
    const track = (object) => {
      if (object) {
        objects.push(object);
        run.objects.add(object);
      }
      return object;
    };
    const fromColor0x = getPlayer0xColor(step.fromColor);
    const toColor0x = getPlayer0xColor(step.toColor);
    const cityHexKeys = step.cityHexKeys.filter((hexKey) => renderer.hexes[hexKey]);
    const cityHexes = cityHexKeys.map((hexKey) => renderer.hexes[hexKey]);

    const beforeBorder = track(renderer.createCityBorderCellsGraphics(affectedHexKeys, before));
    const afterBorder = track(renderer.createCityBorderCellsGraphics(affectedHexKeys, after));
    const beforeFill = track(renderer.fillCityCaptureCells(cityHexKeys, fromColor0x, config.fillAlpha));
    const afterFill = track(renderer.fillCityCaptureCells(cityHexKeys, toColor0x, config.fillAlpha));
    beforeFill.setAlpha(0);

    // Круг прожигания: внутри — город уже нового владельца, снаружи — еще
    // прежнего. Пока круг пуст, слой «после» не виден вовсе
    const burnShape = track(scene.make.graphics({add: false}));
    const insideMask = this.createMask(run, burnShape);
    const outsideMask = this.createMask(run, burnShape);
    outsideMask.setInvertAlpha(true);
    [afterBorder, afterFill].forEach((object) => object.setMask(insideMask));
    [beforeBorder, beforeFill].forEach((object) => object.setMask(outsideMask));

    // Огонь — только по ячейкам самого города
    const fireShape = track(scene.make.graphics({add: false}));
    renderer.fillCityCaptureCells(cityHexKeys, 0xffffff, 1, {graphics: fireShape, scale: config.fire.maskScale});
    const fire = track(scene.add.graphics());
    fire.setDepth(CITY_CAPTURE_LAYER_DEPTHS.fire);
    fire.setMask(this.createMask(run, fireShape));

    const originHex = renderer.hexes[step.originHexKey]
      || renderer.hexes[step.centralQuarterHexKey]
      || cityHexes[0];
    const centralHex = renderer.hexes[step.centralQuarterHexKey];
    const fromIcon = centralHex ? track(this.createOwnerIcon(centralHex, step.fromIconPath, fromColor0x)) : null;
    const toIcon = centralHex ? track(this.createOwnerIcon(centralHex, step.toIconPath, toColor0x)) : null;
    fromIcon?.setAlpha(0);
    toIcon?.setAlpha(0);

    return {
      objects,
      beforeBorder,
      afterBorder,
      beforeFill,
      afterFill,
      burnShape,
      fire,
      sparks: track(this.createSparkEmitter(toColor0x)),
      fromIcon,
      toIcon,
      toColor0x,
      cityHexes,
      origin: {x: originHex.centerX, y: originHex.centerY},
      size: originHex.size,
      radii: calcCityCaptureBurnRadii(originHex, cityHexes, config.fire),
    };
  }

  createMask(run, shape) {
    const mask = shape.createGeometryMask();
    run.masks.add(mask);
    return mask;
  }

  /**
   * Область прожигания и огненное кольцо по ее краю. Край рваный и колышется
   * (buildRaggedRingPoints): по нему же режет маска, так что граница цветов
   * идет ровно под огнем
   */
  drawBurnFront(layers, radius, wobble) {
    const {burnShape, fire, origin, size, toColor0x} = layers;
    const {raggedAmplitudeRatio, raggedPoints, rings} = this.config.fire;
    const amplitude = size * raggedAmplitudeRatio;
    burnShape.clear();
    burnShape.fillStyle(0xffffff, 1);
    burnShape.fillPoints(buildRaggedRingPoints(origin, radius, amplitude, raggedPoints, wobble), true);

    fire.clear();
    rings.forEach(({widthRatio, offsetRatio, color, alpha}) => {
      const ringRadius = radius - size * offsetRatio;
      if (ringRadius <= 0) {
        return;
      }
      fire.lineStyle(size * widthRatio, color === NEW_OWNER_COLOR ? toColor0x : color, alpha);
      fire.strokePoints(buildRaggedRingPoints(origin, ringRadius, amplitude, raggedPoints, wobble), true, true);
    });
  }

  /** Искры в случайных точках фронта — только там, где фронт идет по городу */
  emitSparks(layers, radius) {
    const {sparks, origin, cityHexes} = layers;
    if (!sparks) {
      return;
    }
    for (let spark = 0; spark < this.config.sparks.perFrame; spark += 1) {
      for (let attempt = 0; attempt < 6; attempt += 1) {
        const angle = Math.random() * Math.PI * 2;
        const x = origin.x + Math.cos(angle) * radius;
        const y = origin.y + Math.sin(angle) * radius;
        const isInsideCity = cityHexes.some((hex) => Math.hypot(x - hex.centerX, y - hex.centerY) < hex.size * 0.95);
        if (isInsideCity) {
          sparks.emitParticleAt(x, y, 1);
          break;
        }
      }
    }
  }

  /**
   * Звук прожигания — вместе с огненным фронтом, по очкам города (см.
   * CITY_CAPTURE_ANIMATION.burnSounds). Звук отложенный: пока файл не
   * загружен, облет идет молча
   */
  playBurnSound(step) {
    const soundId = getCityCaptureBurnSoundId(step.cityWinPoints, this.config.burnSounds);
    if (!soundId) {
      return;
    }
    try {
      this.scene.soundManager?.playDeferredSound?.(soundId);
    } catch (error) {
      console.warn('[CityCapture] не удалось проиграть звук прожигания', error);
    }
  }

  /** Город прожжен целиком: остаются слои «после» без масок и новая иконка */
  completeBurn(run, layers) {
    [layers.afterBorder, layers.afterFill].forEach((object) => object.clearMask?.(false));
    layers.toIcon?.setAlpha(1);
    this.destroyObjects(run, [layers.beforeBorder, layers.beforeFill, layers.fromIcon, layers.fire, layers.burnShape]);
  }

  /**
   * Иконка владельца над центральным кварталом — значок его цвета с картинкой
   * фракции/образа, как в модалке захвата. Картинка не загрузилась — значок
   * без нее
   */
  createOwnerIcon(hex, iconPath, color0x) {
    const {scene} = this;
    const {badgeRadiusRatio, imageSizeRatio, outlineWidthRatio, outlineColor, outlineAlpha} = this.config.icon;
    const half = hex.size * badgeRadiusRatio;
    const badge = scene.add.graphics();
    badge.fillStyle(color0x, 1);
    badge.fillRoundedRect(-half, -half, half * 2, half * 2, half * 0.25);
    badge.lineStyle(hex.size * outlineWidthRatio, outlineColor, outlineAlpha);
    badge.strokeRoundedRect(-half, -half, half * 2, half * 2, half * 0.25);
    const children = [badge];

    const textureKey = getIconTextureKey(iconPath);
    if (iconPath && scene.textures.exists(textureKey)) {
      const image = scene.add.image(0, 0, textureKey);
      image.setDisplaySize(hex.size * imageSizeRatio, hex.size * imageSizeRatio);
      children.push(image);
    }

    const container = scene.add.container(hex.centerX, hex.centerY, children);
    container.setDepth(CITY_CAPTURE_LAYER_DEPTHS.icon);
    return container;
  }

  createSparkEmitter(toColor0x) {
    const {scene} = this;
    if (typeof scene.add.particles !== 'function') {
      return null;
    }
    this.ensureSparkTexture();
    const {sparks} = this.config;
    const emitter = scene.add.particles(0, 0, SPARK_TEXTURE_KEY, {
      lifespan: {min: sparks.lifespanMinMs, max: sparks.lifespanMaxMs},
      speed: {min: sparks.speedMin, max: sparks.speedMax},
      angle: {min: 0, max: 360},
      scale: {start: sparks.scaleStart, end: 0},
      alpha: {start: 1, end: 0},
      gravityY: sparks.gravityY,
      tint: sparks.colors.map((color) => (color === NEW_OWNER_COLOR ? toColor0x : color)),
      emitting: false,
    });
    emitter.setDepth(CITY_CAPTURE_LAYER_DEPTHS.sparks);
    return emitter;
  }

  /** Мягкая белая точка для искр — рисуется один раз на игру */
  ensureSparkTexture() {
    const {scene} = this;
    if (scene.textures.exists(SPARK_TEXTURE_KEY)) {
      return;
    }
    const size = this.config.sparks.textureSize;
    const center = size / 2;
    const shape = scene.make.graphics({add: false});
    [[1, 0.25], [0.7, 0.45], [0.4, 1]].forEach(([radiusShare, alpha]) => {
      shape.fillStyle(0xffffff, alpha);
      shape.fillCircle(center, center, center * radiusShare);
    });
    shape.generateTexture(SPARK_TEXTURE_KEY, size, size);
    shape.destroy();
  }

  /**
   * Картинки иконок владельцев грузятся в Phaser на старте облета (в сцене их
   * нет — это картинки интерфейса). Ждем не дольше iconLoadTimeoutMs: без
   * картинки значок все равно покажется
   */
  loadIconTextures(run) {
    const {scene, config} = this;
    const iconPaths = [...new Set(run.steps.flatMap((step) => [step.fromIconPath, step.toIconPath]))]
      .filter((iconPath) => iconPath && !scene.textures.exists(getIconTextureKey(iconPath)));
    if (iconPaths.length === 0 || typeof scene.load?.image !== 'function') {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      const done = () => {
        clearTimeout(timer);
        scene.load.off('complete', done);
        resolve();
      };
      const timer = setTimeout(done, config.iconLoadTimeoutMs);
      iconPaths.forEach((iconPath) => scene.load.image(getIconTextureKey(iconPath), iconPath));
      scene.load.once('complete', done);
      scene.load.start();
    });
  }

  getStepHexes(step) {
    const {hexes} = this.scene.hexGridRenderer;
    return step.cityHexKeys.map((hexKey) => hexes[hexKey]).filter(Boolean);
  }

  getStepCenter(step) {
    const bounds = calcHexListBounds(this.getStepHexes(step));
    return bounds ? {x: bounds.centerX, y: bounds.centerY} : null;
  }

  getStepCameraFrame(step) {
    const {scene, config} = this;
    const freeAreaPx = scene.getFreeAreaSizePx();
    const frame = calcCityCaptureCameraFrame(this.getStepHexes(step), freeAreaPx, config);
    const hexSize = this.getStepHexes(step)[0]?.size || 0;
    const playArea = scene.calcPlayAreaBounds?.(scene.store.getState()) || null;
    return clampCameraFrameToBounds(frame, freeAreaPx, playArea, hexSize * config.fieldPaddingRatio);
  }

  /**
   * Пауза перед переездом камеры (CITY_CAPTURE_ANIMATION.settleMs) и еще один
   * кадр: переезд начинается, когда основной поток отработал перерисовку
   * поля и интерфейса, а не посреди нее
   */
  async settle(run) {
    await this.runTween(run, this.config.settleMs);
    await this.runTween(run, 1);
  }

  /** Плавный переезд камеры: центр свободной области и зум — к кадру `target` */
  moveCamera(run, target, durationMs) {
    const {scene} = this;
    const camera = scene.cameras.main;
    const from = scene.getCameraFrame();
    return this.runTween(run, durationMs, (t) => {
      const frame = interpolateCameraFrame(from, target, easeInOutSine(t));
      camera.setZoom(frame.zoom);
      scene.centerOnFreeArea(frame.x, frame.y);
    });
  }

  /**
   * Подмена владельцев на поле (см. buildCityCaptureOverrides) и перерисовка
   * поля с ней. `redrawInPlace` — перерисовать на месте только то, что
   * подмена поменяла (полная пересборка поля — тысячи объектов, на телефоне
   * это рывок камеры или огня); вернул false — поле пересоберется целиком
   * перед ближайшим кадром. Без него — тоже целиком
   * @param {Object} run
   * @param {Object} params - см. buildCityCaptureOverrides
   * @param {{redraw?: boolean, redrawInPlace?: Function}} [options]
   */
  applyOverrides(run, params, {redraw = true, redrawInPlace = null} = {}) {
    if (run.isCancelled) {
      return;
    }
    const {scene} = this;
    scene.hexGridRenderer.setCityCaptureOverrides(buildCityCaptureOverrides(params));
    if (!redraw) {
      return;
    }
    let isRedrawnInPlace = false;
    try {
      isRedrawnInPlace = Boolean(redrawInPlace?.());
    } catch (error) {
      console.warn('[CityCapture] не удалось перерисовать город на месте', error);
    }
    if (isRedrawnInPlace) {
      return;
    }
    if (typeof scene.requestStoreUpdate === 'function') {
      scene.requestStoreUpdate({isForced: true});
    } else {
      scene.handleStoreUpdate();
    }
  }

  /**
   * Твин-счетчик от 0 до 1 за `durationMs`; без `onUpdate` — просто пауза.
   * Обрыв облета отклоняет ожидание (CANCELLED)
   */
  runTween(run, durationMs, onUpdate) {
    return new Promise((resolve, reject) => {
      if (run.isCancelled) {
        reject(CANCELLED);
        return;
      }
      const entry = {reject, stop: null};
      const tween = this.scene.tweens.addCounter({
        from: 0,
        to: 1,
        duration: Math.max(1, durationMs),
        onUpdate: (counter) => {
          if (!run.isCancelled) {
            onUpdate?.(counter.getValue());
          }
        },
        onComplete: () => {
          run.pending.delete(entry);
          if (run.isCancelled) {
            return;
          }
          onUpdate?.(1);
          resolve();
        },
      });
      entry.stop = () => tween?.stop?.();
      run.pending.add(entry);
    });
  }

  /** Дождаться промиса, но выйти, если облет за это время оборвали */
  guard(run, promise) {
    return promise.then(() => {
      if (run.isCancelled) {
        throw CANCELLED;
      }
    });
  }

  /**
   * Сторож: облет не кончился за ожидаемое время с запасом (вкладка в фоне
   * останавливает твины, потерянный WebGL) — обрываем, модалка показывается
   */
  getMaxDurationMs(run) {
    const {iconLoadTimeoutMs, watchdogSlowdownFactor, watchdogPaddingMs} = this.config;
    return (getCityCaptureAnimationDurationMs(run.steps.length, this.config) + iconLoadTimeoutMs) * watchdogSlowdownFactor
      + watchdogPaddingMs;
  }

  /**
   * Сетевая партия: вкладку скрыли посреди облета, а вернули, когда окно
   * облета во времени хода уже кончилось (см. canPlay), — облет обрываем,
   * сразу модалка захвата и кадр игрока. Окно отсчитывается от прихода
   * захвата сюда, а не от его серверного времени: у клиента, приславшего
   * захват, оно до ответа сервера еще прежнее (см. SET_CITY_CAPTURE_TIME_WINDOW),
   * а захват доходит до остальных за доли секунды
   * @param {Object} run
   * @param {Object} state
   * @param {Object[]} capturedCities - все захваты раунда: окно — по ним
   */
  watchNetworkWindow(run, state, capturedCities) {
    if (selectGameType(state) !== GAME_TYPES.ONLINE || typeof document === 'undefined') {
      return;
    }
    const windowEndsAt = Date.now() + getCityCaptureAnimationDurationMs(capturedCities.length, this.config);
    run.onVisibilityChange = () => {
      if (this.run === run && !checkIfPageHidden() && Date.now() >= windowEndsAt) {
        this.abort({restoreCamera: true});
      }
    };
    document.addEventListener('visibilitychange', run.onVisibilityChange);
  }

  armWatchdog(run) {
    const timeoutMs = this.getMaxDurationMs(run);
    run.watchdog = setTimeout(() => {
      if (this.run === run) {
        console.warn('[CityCapture] облет не уложился во время — обрываем');
        this.abort({restoreCamera: true});
      }
    }, timeoutMs);
  }

  /** Все города показаны — очередь модалок идет дальше (модалка захвата) */
  finishQueue(run) {
    if (run.isQueueFinished) {
      return;
    }
    run.isQueueFinished = true;
    this.scene.hexGridRenderer?.setCityCaptureOverrides(null);
    this.scene.store.dispatch(finishCityCaptureAnimation());
  }

  destroyObjects(run, objects) {
    objects.forEach((object) => {
      if (!object) {
        return;
      }
      run.objects.delete(object);
      try {
        object.clearMask?.(false);
        object.destroy?.();
      } catch (error) {
        // Объект уже разобран вместе со сценой
      }
    });
  }

  destroyMasks(run) {
    run.masks.forEach((mask) => {
      try {
        mask.destroy?.();
      } catch (error) {
        // Маска уже разобрана вместе со сценой
      }
    });
    run.masks.clear();
  }

  cleanup(run) {
    clearTimeout(run.watchdog);
    if (run.onVisibilityChange) {
      document.removeEventListener('visibilitychange', run.onVisibilityChange);
      run.onVisibilityChange = null;
    }
    markAwaitedAnimationFinished(AWAITED_ANIMATION_KEY);
    this.destroyObjects(run, [...run.objects]);
    this.destroyMasks(run);
    run.isInputLocked = false;
    if (this.run === run) {
      this.run = null;
    }
  }

  /**
   * Облет уже показал города и везет камеру назад, а следующей анимации (рост
   * дохода руин) пора начинаться: кадр игрока переходит к ней
   * @returns {?{x: number, y: number, zoom: number}} кадр игрока; null — облета нет
   */
  handOverCamera() {
    const savedCamera = this.run?.savedCamera || null;
    this.abort();
    return savedCamera;
  }

  /**
   * Оборвать облет: убрать слои, снять подмену, отдать очередь модалкам.
   * `restoreCamera` — сразу вернуть кадр игрока (сцена жива, а облет
   * сорвался); при разборе сцены камеру возвращать некуда
   */
  abort({restoreCamera = false} = {}) {
    const run = this.run;
    if (!run) {
      return;
    }
    run.isCancelled = true;
    run.pending.forEach((entry) => {
      entry.stop?.();
      entry.reject(CANCELLED);
    });
    run.pending.clear();
    if (restoreCamera && run.savedCamera && this.scene.cameras?.main) {
      this.scene.cameras.main.setZoom(run.savedCamera.zoom);
      this.scene.centerOnFreeArea(run.savedCamera.x, run.savedCamera.y);
    }
    this.cleanup(run);
    this.finishQueue(run);
  }
}
